import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  LogOut,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Separator } from '@/Components/ui/separator';
import { cn } from '@/lib/utils';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';

// Recruiter top-nav items. "Find Candidate" and "Support" were
// previously listed here but their routes were never built — leaving
// dead 404 links in production. Removed until the actual pages ship.
const NAV_ITEMS = [
  { to: '/recruiter/dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
  { to: '/recruiter/my-jobs',      label: 'All Jobs',     icon: Briefcase },
  { to: '/recruiter/applications', label: 'Applications', icon: Users },
];

export default function RecruiterNavbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, recruiter } = useRecruiterAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      // ignore — navigate regardless
    }
    navigate('/recruiter/login');
  };

  // First letter of recruiter email/name for avatar fallback
  const initial = (recruiter?.email || 'R').trim().charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-30 w-full border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="mx-auto flex h-14 w-full max-w-7xl items-center gap-2 px-4">
        {/* Logo */}
        <button
          onClick={() => navigate('/recruiter/dashboard')}
          className="flex items-center gap-2 mr-4 shrink-0"
          aria-label="Go to dashboard"
        >
          <img
            src="/Newzenalphablue.png"
            alt="Logo"
            className="h-8 w-auto object-contain"
          />
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
            const active = pathname === to || pathname.startsWith(to + '/');
            return (
              <Button
                key={to}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate(to)}
                className={cn(
                  'h-9 px-3 text-sm font-medium',
                  active
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="mr-2 h-4 w-4" strokeWidth={2} />
                {label}
              </Button>
            );
          })}
        </div>

        <div className="flex-1" />

        {/* Right cluster: avatar + logout */}
        <div className="flex items-center gap-2">
          <div
            className="hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold"
            aria-label="Account"
          >
            {initial}
          </div>
          <Separator orientation="vertical" className="hidden sm:block h-6" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="h-9 px-3 text-sm text-muted-foreground hover:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" strokeWidth={2} />
            Logout
          </Button>
        </div>
      </nav>

      {/* Mobile links — visible under md */}
      <div className="md:hidden border-t border-border/60 overflow-x-auto">
        <div className="flex items-center gap-1 px-2 py-1">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => {
            const active = pathname === to || pathname.startsWith(to + '/');
            return (
              <Button
                key={to}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate(to)}
                className="h-8 shrink-0 px-2 text-xs"
              >
                <Icon className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                {label}
              </Button>
            );
          })}
        </div>
      </div>
    </header>
  );
}
